/**
 * Phaser → React bounds reporting for the tutorial spotlight.
 *
 * React never reaches into the scene: it dispatches
 * `tutorial:request-bounds` with the target ids it needs, and the reporter
 * (attached by GameScene ONLY in scripted-tutorial presentation) answers
 * with `tutorial:bounds` carrying viewport-space rects for the REAL game
 * objects — computed from each object's transform-aware world bounds plus
 * the live canvas position (rendering is 1:1: world px == canvas CSS px,
 * settled decision).
 *
 * Supported target ids:
 *   hand-rack            — union of the player's rack tile sprites
 *   hand-tile:<id>       — one rack tile
 *   board-tile:<id>      — one placed board tile
 *   placement-previews   — every live ghost destination (0..2 rects)
 *   seat:<position>      — a seat's rendered player card (0-3)
 *   knock                — the PASS control
 *
 * The reporter re-answers the last request on scene resize; React also
 * re-requests on its own cadence (animations move sprites), so bounds are
 * eventually consistent without any tutorial branching inside gameplay code.
 */
import type { SpotRect } from './spotlight/spotlightGeometry';

export const REQUEST_BOUNDS_EVENT = 'tutorial:request-bounds';
export const BOUNDS_EVENT = 'tutorial:bounds';

export interface BoundsRequestDetail { targets: string[] }
export interface BoundsDetail { bounds: Record<string, SpotRect[]> }

/** World-space rectangle (Phaser.Geom.Rectangle shape). */
interface WorldRect { x: number; y: number; width: number; height: number }

export interface BoundsSources {
  /** Rect of one rack tile (world space), or null when absent. */
  getHandTileBounds(tileId: number): WorldRect | null;
  /** Union rect of the whole rack (world space), or null when empty. */
  getHandRackBounds(): WorldRect | null;
  /** Rect of one placed board tile (world space), or null. */
  getBoardTileBounds(tileId: number): WorldRect | null;
  /** Rects of the live placement previews (world space). */
  getPlacementPreviewBounds(): WorldRect[];
  /** Rect of one seat's rendered player card (world space), or null. */
  getSeatBounds(position: number): WorldRect | null;
  /** Rect of the PASS control (world space), or null. */
  getKnockBounds(): WorldRect | null;
  /** The live canvas element (for viewport offset). */
  getCanvas(): HTMLCanvasElement | null;
}

function toViewport(rect: WorldRect, canvas: DOMRect): SpotRect {
  return { x: canvas.left + rect.x, y: canvas.top + rect.y, w: rect.width, h: rect.height };
}

export function measureTargets(sources: BoundsSources, targets: string[]): Record<string, SpotRect[]> {
  const canvasEl = sources.getCanvas();
  if (!canvasEl) return {};
  const canvas = canvasEl.getBoundingClientRect();
  const out: Record<string, SpotRect[]> = {};
  for (const id of targets) {
    let rects: WorldRect[] = [];
    if (id === 'hand-rack') {
      const r = sources.getHandRackBounds();
      rects = r ? [r] : [];
    } else if (id === 'placement-previews') {
      rects = sources.getPlacementPreviewBounds();
    } else if (id.startsWith('hand-tile:')) {
      const r = sources.getHandTileBounds(Number(id.slice('hand-tile:'.length)));
      rects = r ? [r] : [];
    } else if (id.startsWith('board-tile:')) {
      const r = sources.getBoardTileBounds(Number(id.slice('board-tile:'.length)));
      rects = r ? [r] : [];
    } else if (id.startsWith('seat:')) {
      const r = sources.getSeatBounds(Number(id.slice('seat:'.length)));
      rects = r ? [r] : [];
    } else if (id === 'knock') {
      const r = sources.getKnockBounds();
      rects = r ? [r] : [];
    }
    out[id] = rects
      .filter((r) => r.width > 0 && r.height > 0)
      .map((r) => toViewport(r, canvas));
  }
  return out;
}

/**
 * Attach the reporter. Returns a detach function (GameScene calls it from
 * cleanup). `onSceneResize` lets the scene's own resize event trigger a
 * re-answer of the last request without React noticing the resize first.
 */
export function attachTutorialBoundsReporter(
  sources: BoundsSources,
  resizeEmitter?: { on(ev: string, cb: () => void): void; off(ev: string, cb: () => void): void },
): () => void {
  let lastTargets: string[] = [];

  const answer = () => {
    if (lastTargets.length === 0) return;
    window.dispatchEvent(new CustomEvent<BoundsDetail>(BOUNDS_EVENT, {
      detail: { bounds: measureTargets(sources, lastTargets) },
    }));
  };

  const onRequest = (e: Event) => {
    const targets = (e as CustomEvent<BoundsRequestDetail>).detail?.targets;
    if (!Array.isArray(targets)) return;
    lastTargets = targets;
    answer();
  };

  window.addEventListener(REQUEST_BOUNDS_EVENT, onRequest);
  resizeEmitter?.on('resize', answer);
  return () => {
    window.removeEventListener(REQUEST_BOUNDS_EVENT, onRequest);
    resizeEmitter?.off('resize', answer);
  };
}
